import type { AppConfig } from './types/AppConfig';
import type { AuthConfig } from './types/AuthConfig';
import type { KeycloakConfig } from 'keycloak-js';

import '@mdi/font/css/materialdesignicons.min.css';
import '~/assets/css/main.css';
import '~/modules/validation';
import App from './App.vue';
import PrimeVue from 'primevue/config';
import Ripple from 'primevue/ripple';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import VueScreen from 'vue-screen';
import authService from '~/services/authService';
import authStorePlugin from './plugins/authStorePlugin';
import i18n from '~/modules/i18n';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import primeVueConfig from '~/components/Tangerine/primevue';
import router from '~/modules/router';
import { FeatureFlagOptions, TgnFeatureFlag } from '~/plugins/featureFlagPlugin';
import { URL } from './types/CommonTypes';
import { createApp } from 'vue';
import { createPinia, setActivePinia } from 'pinia';
import { eventPlugin } from '~/plugins/eventPlugin';
import { useNavigationStore } from '~/stores/useNavigationStore';
import { vMaska } from 'maska';

const pinia = createPinia();
setActivePinia(pinia);
pinia.use(piniaPluginPersistedstate);

const config: AuthConfig & AppConfig & { featureFlagURL: URL } = await preMount(
  (config: AppConfig & AuthConfig): AppConfig => config,
);
const kcConfig: KeycloakConfig = omit(config, ['baseURL', 'accountsURL']);

const app = createApp(App);
app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);
app.use(pinia);
app.use(authStorePlugin, { pinia });
app.use(i18n);
app.use(router);
app.use(ToastService);
app.use(PrimeVue, primeVueConfig);
app.use(eventPlugin, pinia);

app.config.errorHandler = (err, instance, info) => {
  console.error('Global error:', err);
  console.log('Vue Instance:', instance);
  console.log('Error info:', info);
};

function renderApp() {
  const APP_STORE = useAppStore();
  const NAVIGATION_STORE = useNavigationStore();
  APP_STORE.setAppConfig(config);

  app.use(TgnFeatureFlag, {
    sUrl: APP_STORE.featureFlagURL!,
    callback: NAVIGATION_STORE.setFeatureFlag,
  } as FeatureFlagOptions);
  app.directive('maska', vMaska);
  app.use(VueScreen, 'tailwind');
  app.mount('#app');
}

authService.CallInit(kcConfig, renderApp);
